import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MathRenderer from '../components/math/MathRenderer';
import { GraphChoice, skillAPI, SkillDefinition, SkillProblem } from '../services/api';
import {
  GRAPH_FUNCTION_TYPES,
  GRAPH_TRANSFORMATION_COUNTS,
  GraphTransformationCount,
  buildSkillSubtypeParams,
  getInitialSelectedSkillSubtypes,
  getSkillSubtypeOptions,
  toggleSelectedSkillSubtype,
} from '../utils/skillPractice';

const DEFAULT_PROBLEM_COUNT = 10;

const isSafeGraphSvg = (value?: string) => {
  if (!value) return false;
  const trimmed = value.trim();
  return (
    trimmed.startsWith('<svg') &&
    trimmed.endsWith('</svg>') &&
    !/<script\b/i.test(trimmed) &&
    !/\son[a-z]+\s*=/i.test(trimmed) &&
    !/javascript:/i.test(trimmed)
  );
};

const SKILL_FALLBACKS: Record<string, SkillDefinition> = {
  differentiation: {
    slug: 'differentiation',
    name: 'Differentiation',
    description: 'Chain, product, and quotient rule practice.',
  },
  trigonometry: {
    slug: 'trigonometry',
    name: 'Trigonometry',
    description: 'Solve trigonometric equations in a given domain.',
  },
  integration: {
    slug: 'integration',
    name: 'Integration',
    description: 'Substitution, parts, and partial fractions practice.',
  },
  logarithms: {
    slug: 'logarithms',
    name: 'Logarithms',
    description: 'Solve logarithmic equations.',
  },
  exponential: {
    slug: 'exponential',
    name: 'Exponential',
    description: 'Solve exponential equations with varied structures.',
  },
  quadratic: {
    slug: 'quadratic',
    name: 'Quadratic',
    description: 'Solve quadratic equations (monic and non-monic).',
  },
  cubic: {
    slug: 'cubic',
    name: 'Cubic',
    description: 'Solve cubic equations by factoring.',
  },
  algebra: {
    slug: 'algebra',
    name: 'Algebra',
    description: 'Expansion, factorisation, and linear equations practice.',
  },
  transformations: {
    slug: 'transformations',
    name: 'Transformations',
    description: 'Apply or identify function transformations.',
  },
  graph_sketching: {
    slug: 'graph_sketching',
    name: 'Graph Sketching',
    description: 'Sketch transformed graphs for common function types.',
  },
};

const SkillPractice: React.FC = () => {
  const { skill = '' } = useParams<{ skill: string }>();
  const [skillDefinition, setSkillDefinition] = useState<SkillDefinition | null>(null);
  const [problems, setProblems] = useState<SkillProblem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problemCount, setProblemCount] = useState(DEFAULT_PROBLEM_COUNT);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [graphChoiceAttempts, setGraphChoiceAttempts] = useState<Record<string, { selectedLabel: string; isCorrect: boolean }>>({});
  const [graphTransformationCount, setGraphTransformationCount] = useState<GraphTransformationCount>('2');
  const [selectedSubtypesBySkill, setSelectedSubtypesBySkill] = useState<Record<string, string[]>>({});

  const activeSubtypeOptions = useMemo(() => getSkillSubtypeOptions(skill), [skill]);
  const selectedSubtypes = useMemo(
    () => selectedSubtypesBySkill[skill] ?? getInitialSelectedSkillSubtypes(skill),
    [selectedSubtypesBySkill, skill]
  );

  const activeSkill = skillDefinition ?? SKILL_FALLBACKS[skill] ?? null;

  const fetchSkillDefinition = useCallback(async () => {
    try {
      const skills = await skillAPI.getSkills();
      const match = skills.find((item) => item.slug === skill);
      setSkillDefinition(match ?? null);
    } catch (err) {
      setSkillDefinition(SKILL_FALLBACKS[skill] ?? null);
    }
  }, [skill]);

  const buildParams = useCallback(() => {
    if (!activeSubtypeOptions.length) {
      return { number_of_problems: problemCount };
    }
    return buildSkillSubtypeParams(
      skill,
      selectedSubtypes,
      problemCount,
      skill === 'graph_sketching' ? graphTransformationCount : undefined
    );
  }, [activeSubtypeOptions.length, graphTransformationCount, problemCount, selectedSubtypes, skill]);

  const loadProblems = useCallback(async () => {
    if (activeSubtypeOptions.length > 0 && selectedSubtypes.length === 0) {
      setError('Select at least one question type to generate problems.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await skillAPI.getSkillProblems(skill, buildParams());
      setProblems(response.problems ?? []);
    } catch (err) {
      setError('Failed to generate problems. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [activeSubtypeOptions.length, buildParams, selectedSubtypes.length, skill]);

  const downloadWorksheet = useCallback(async () => {
    if (activeSubtypeOptions.length > 0 && selectedSubtypes.length === 0) {
      setError('Select at least one question type to download a worksheet.');
      return;
    }
    setError(null);
    try {
      const blob = await skillAPI.downloadSkillWorksheetPdf(skill, buildParams());
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = `${skill}_worksheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (err) {
      setError('Failed to download worksheet. Please try again.');
    }
  }, [activeSubtypeOptions.length, buildParams, selectedSubtypes.length, skill]);

  useEffect(() => {
    fetchSkillDefinition();
  }, [fetchSkillDefinition]);

  useEffect(() => {
    setProblems([]);
    setError(null);
    setIsLoading(false);
  }, [skill]);

  useEffect(() => {
    setOpenItems({});
    setGraphChoiceAttempts({});
  }, [problems]);

  const getGraphCatalogQuestionId = useCallback((problem: SkillProblem) => (
    problem.catalog_question_id ?? problem.question_id ?? problem.metadata?.catalog_question_id ?? null
  ), []);

  const recordGraphChoiceAttempt = useCallback((problem: SkillProblem, choice: GraphChoice) => {
    const questionId = getGraphCatalogQuestionId(problem);
    if (!questionId) {
      return;
    }
    void skillAPI.recordGraphSketchingAnswer({
      question_id: questionId,
      selected_choice_label: choice.label,
    }).catch(() => undefined);
  }, [getGraphCatalogQuestionId]);

  const selectGraphChoice = useCallback((itemId: string, problem: SkillProblem, choice: GraphChoice) => {
    if (!graphChoiceAttempts[itemId]) {
      recordGraphChoiceAttempt(problem, choice);
    }
    setGraphChoiceAttempts((prev) => {
      if (prev[itemId]) {
        return prev;
      }
      return {
        ...prev,
        [itemId]: { selectedLabel: choice.label, isCorrect: choice.is_correct },
      };
    });
    setOpenItems((prev) => ({ ...prev, [itemId]: true }));
  }, [graphChoiceAttempts, recordGraphChoiceAttempt]);

  const problemList = useMemo(() => problems ?? [], [problems]);

  const toggleSubtype = useCallback(
    (value: string) => {
      setError(null);
      setSelectedSubtypesBySkill((prev) => {
        const current = prev[skill] ?? getInitialSelectedSkillSubtypes(skill);
        return {
          ...prev,
          [skill]: toggleSelectedSkillSubtype(current, value),
        };
      });
    },
    [skill]
  );

  const selectAllSubtypes = useCallback(() => {
    setError(null);
    setSelectedSubtypesBySkill((prev) => ({
      ...prev,
      [skill]: getInitialSelectedSkillSubtypes(skill),
    }));
  }, [skill]);

  if (!activeSkill) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Skill not found</h1>
          <Link to="/dashboard" className="text-subject-600 hover:text-subject-500 text-sm">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{activeSkill.name}</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{activeSkill.description}</p>
            </div>
            <Link
              to="/dashboard"
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-subject-600 dark:hover:text-subject-400"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Number of problems</label>
                <div className="mt-2 relative w-40">
                  <select
                    value={problemCount}
                    onChange={(event) => setProblemCount(Number(event.target.value))}
                    className="block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 pr-9 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-subject-500 focus:outline-none focus:ring-2 focus:ring-subject-200 dark:focus:ring-subject-800"
                  >
                    {[5, 10, 15, 20].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              {skill === 'graph_sketching' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Transformations</label>
                  <div className="mt-2 relative w-36">
                    <select
                      value={graphTransformationCount}
                      onChange={(event) => setGraphTransformationCount(event.target.value as GraphTransformationCount)}
                      className="block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 pr-9 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-subject-500 focus:outline-none focus:ring-2 focus:ring-subject-200 dark:focus:ring-subject-800"
                    >
                      {GRAPH_TRANSFORMATION_COUNTS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={loadProblems}
                disabled={isLoading}
                className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Generating...' : 'Generate Problems'}
              </button>
              <button
                onClick={downloadWorksheet}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:border-subject-500 hover:text-subject-600"
              >
                Download Worksheet (PDF)
              </button>
            </div>
          </div>

          {activeSubtypeOptions.length > 1 && (
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Question types
                </span>
                <button
                  type="button"
                  onClick={selectAllSubtypes}
                  className="text-xs text-subject-600 hover:text-subject-500 font-medium"
                >
                  Select all
                </button>
              </div>
              <div className={skill === 'graph_sketching' ? 'grid grid-cols-2 sm:grid-cols-3 gap-3' : 'flex flex-wrap gap-3'}>
                {(skill === 'graph_sketching' ? GRAPH_FUNCTION_TYPES : activeSubtypeOptions).map((option) => {
                  const isChecked = selectedSubtypes.includes(option.value);
                  return (
                    <label key={option.value} className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSubtype(option.value)}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          )}
        </div>

        <div className="space-y-4">
          {problemList.map((problem, index) => {
            const itemId = `${index}`;
            const isOpen = !!openItems[itemId];
            const isGraphChoice = problem.mode === 'graph_choice' && Array.isArray(problem.choices);
            const graphAttempt = graphChoiceAttempts[itemId];
            return (
              <div key={`${problem.problem_latex}-${index}`} className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-gray-900 dark:text-white text-base">
                    <MathRenderer latex={problem.problem_latex} />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {problem.marks ? `${problem.marks} mark${problem.marks === 1 ? '' : 's'}` : ''}
                  </span>
                </div>
	                {isGraphChoice && (
	                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
	                    {problem.choices!.map((choice) => {
                        const isSelected = graphAttempt?.selectedLabel === choice.label;
                        const isCorrectChoice = Boolean(graphAttempt) && choice.is_correct;
                        const isWrongSelected = Boolean(graphAttempt) && isSelected && !choice.is_correct;
                        return (
	                      <button
	                        key={choice.label}
                          type="button"
                          disabled={Boolean(graphAttempt)}
                          onClick={() => selectGraphChoice(itemId, problem, choice)}
	                        className={`rounded-lg border-2 p-3 text-left transition-colors ${
                            isCorrectChoice
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : isWrongSelected
                                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                : isSelected
                                  ? 'border-subject-500 bg-subject-50 dark:bg-subject-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-subject-400'
                          }`}
	                      >
	                        <div className="mb-2 flex items-center justify-between text-sm font-semibold text-gray-700 dark:text-gray-200">
	                          <span>Graph {choice.label}</span>
	                          {isCorrectChoice ? <span className="text-green-700 dark:text-green-300">Correct</span> : null}
                            {isWrongSelected ? <span className="text-red-700 dark:text-red-300">Selected</span> : null}
	                        </div>
                        {isSafeGraphSvg(choice.svg) ? (
                          <div
                            className="w-full [&_svg]:w-full [&_svg]:h-auto"
                            aria-label={`Graph ${choice.label}`}
                            dangerouslySetInnerHTML={{ __html: choice.svg!.trim() }}
                          />
                        ) : (
                          <MathRenderer latex={choice.tikz_code} />
                        )}
	                      </button>
                        );
                      })}
	                  </div>
	                )}
                <div className="mt-4 flex justify-start">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }))
                    }
                    className="px-3 py-1 text-sm font-medium rounded-full border border-blue-200 text-blue-700 dark:text-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    {isOpen ? 'Hide Answer' : 'Show Answer'}
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 text-gray-900 dark:text-white">
                    <MathRenderer
                      latex={problem.solution_latex}
                      className={skill === 'graph_sketching' ? 'whitespace-pre-line' : ''}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {!isLoading && problemList.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No problems generated yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillPractice;
